import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { db } from '../../lib/firebase';
import { collection, getDocs, query, where, onSnapshot } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { ArrowLeft, Calendar, MapPin, Clock, CheckCircle, AlertCircle } from 'lucide-react';

export default function AdminReservations() {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [branches, setBranches] = useState([]);
  const [reservationCounts, setReservationCounts] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && (!user || !isAdmin)) {
      navigate('/login');
    }
  }, [user, authLoading, isAdmin, navigate]);

  useEffect(() => {
    fetchBranches();
  }, []);

  const fetchBranches = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'branches'));
      const branchesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setBranches(branchesData);

      // Set up listeners for reservation counts
      branchesData.forEach(branch => {
        setupReservationCountListener(branch.id);
      });
    } catch (error) {
      console.error('Error fetching branches:', error);
    } finally {
      setLoading(false);
    }
  };

  const setupReservationCountListener = (branchId) => {
    const today = new Date().toISOString().split('T')[0];
    
    // Listen to pending reservations
    const pendingQuery = query(
      collection(db, 'reservations'),
      where('branchId', '==', branchId),
      where('status', '==', 'pending')
    );

    onSnapshot(pendingQuery, (snapshot) => {
      setReservationCounts(prev => ({
        ...prev,
        [branchId]: {
          ...prev[branchId],
          pending: snapshot.size
        }
      }));
    });

    // Listen to today's confirmed reservations
    const todayQuery = query(
      collection(db, 'reservations'),
      where('branchId', '==', branchId),
      where('status', '==', 'confirmed'),
      where('date', '==', today)
    );

    onSnapshot(todayQuery, (snapshot) => {
      setReservationCounts(prev => ({
        ...prev,
        [branchId]: {
          ...prev[branchId],
          today: snapshot.size
        }
      }));
    });
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#050000] pt-20 flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-red-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050000] pt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/admin')}
            className="text-gray-400 hover:text-white"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-2">
              <Calendar className="w-6 h-6 text-red-500" />
              Rezervasiyalar
            </h1>
            <p className="text-gray-400 text-sm">Filial seçin</p>
          </div>
        </div>

        {/* Branches List */}
        {branches.length === 0 ? (
          <div className="text-center py-16">
            <MapPin className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl text-white mb-2">Filial yoxdur</h3>
            <p className="text-gray-400">Əvvəlcə filial əlavə edin</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {branches.map((branch, index) => {
              const counts = reservationCounts[branch.id] || { pending: 0, today: 0 };
              const hasPending = counts.pending > 0;

              return (
                <motion.div
                  key={branch.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => navigate(`/admin/reservations/${branch.id}`)}
                  className={`bg-[#0F0F0F] border rounded-xl p-6 cursor-pointer transition-all hover:scale-[1.02] ${
                    hasPending ? 'border-yellow-500/50 shadow-lg shadow-yellow-500/10' : 'border-white/10 hover:border-red-500/30'
                  }`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                        hasPending ? 'bg-yellow-500/20' : 'bg-red-500/10'
                      }`}>
                        <MapPin className={`w-6 h-6 ${hasPending ? 'text-yellow-500' : 'text-red-500'}`} />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">{branch.name}</h3>
                        <p className="text-gray-500 text-sm">{branch.address}</p>
                      </div>
                    </div>
                    {hasPending && (
                      <div className="bg-yellow-500 text-black text-xs font-bold px-2 py-1 rounded-full animate-pulse">
                        {counts.pending} YENİ
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className={`p-3 rounded-lg ${hasPending ? 'bg-yellow-500/10' : 'bg-white/5'}`}>
                      <div className="flex items-center gap-2 text-sm">
                        <AlertCircle className={`w-4 h-4 ${hasPending ? 'text-yellow-500' : 'text-gray-500'}`} />
                        <span className={hasPending ? 'text-yellow-400' : 'text-gray-400'}>Gözləyən</span>
                      </div>
                      <div className={`text-2xl font-bold ${hasPending ? 'text-yellow-400' : 'text-gray-400'}`}>
                        {counts.pending}
                      </div>
                    </div>
                    <div className="bg-white/5 p-3 rounded-lg">
                      <div className="flex items-center gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span className="text-gray-400">Bu gün</span>
                      </div>
                      <div className="text-2xl font-bold text-green-400">
                        {counts.today}
                      </div>
                    </div>
                  </div>

                  <Button 
                    className="w-full mt-4 bg-white/5 hover:bg-white/10 text-white"
                  >
                    Rezervasiyalara bax
                  </Button>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
